import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { getMqttClient, TOPICS, publishCmd, publishCamCmd, reconnectMqtt as mqttReconnect } from "../lib/mqtt";
import { DEFAULT_SETTINGS } from "../lib/constants";
import { toast } from "sonner";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const loadLS = (key, fallback) => {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; }
  catch { return fallback; }
};

const MAX_HISTORY = 360; // ~12 minutes of 2s readings

export function AppProvider({ children }) {
  const [theme, setTheme]               = useState(() => loadLS("cem_theme", "dark"));
  const [telemetry, setTelemetry]       = useState(null);
  const [history, setHistory]           = useState([]);
  const [mqttConnected, setMqttConnected] = useState(false);
  const [lastUpdate, setLastUpdate]     = useState(null);
  const [settings, setSettings]         = useState(() => {
    const saved = loadLS("cem_settings", {});
    const merged = { ...DEFAULT_SETTINGS, ...saved };
    // Don't let a previously-saved empty value wipe out a newly-seeded credential
    if (!merged.geminiKeys)   merged.geminiKeys   = DEFAULT_SETTINGS.geminiKeys;
    if (!merged.mqttPass)     merged.mqttPass     = DEFAULT_SETTINGS.mqttPass;
    if (!merged.mqttUser)     merged.mqttUser     = DEFAULT_SETTINGS.mqttUser;
    if (!merged.mqttHost)     merged.mqttHost     = DEFAULT_SETTINGS.mqttHost;
    if (!merged.grafanaUrl)   merged.grafanaUrl   = DEFAULT_SETTINGS.grafanaUrl;
    if (!merged.deepseekKey)    merged.deepseekKey    = DEFAULT_SETTINGS.deepseekKey;
    if (!merged.elevenKey)      merged.elevenKey      = DEFAULT_SETTINGS.elevenKey;
    if (!merged.elevenVoiceId)  merged.elevenVoiceId  = DEFAULT_SETTINGS.elevenVoiceId;
    if (!merged.azureKey)       merged.azureKey       = DEFAULT_SETTINGS.azureKey;
    if (!merged.azureRegion)    merged.azureRegion    = DEFAULT_SETTINGS.azureRegion;
    if (!merged.azureVoice)     merged.azureVoice     = DEFAULT_SETTINGS.azureVoice;
    if (!merged.voicerssKey)    merged.voicerssKey    = DEFAULT_SETTINGS.voicerssKey;
    if (!merged.voicerssLang)   merged.voicerssLang   = DEFAULT_SETTINGS.voicerssLang;
    if (!merged.voicerssVoice)  merged.voicerssVoice  = DEFAULT_SETTINGS.voicerssVoice;
    return merged;
  });
  const [snapshots, setSnapshots]       = useState(() => loadLS("cem_gallery", []));
  const [camImage, setCamImage]         = useState(null);
  const [camState, setCamState]         = useState(null);
  const [lastMotion, setLastMotion]     = useState(null);
  const [mqttRevision, setMqttRevision] = useState(0);
  const clientRef = useRef(null);
  const settingsRef = useRef(settings);
  const alertCooldown = useRef({});
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  // persist theme
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark"); else root.classList.remove("dark");
    localStorage.setItem("cem_theme", JSON.stringify(theme));
  }, [theme]);

  useEffect(() => { localStorage.setItem("cem_settings", JSON.stringify(settings)); }, [settings]);
  useEffect(() => { localStorage.setItem("cem_gallery", JSON.stringify(snapshots)); }, [snapshots]);

  // MQTT connection - re-runs when mqttRevision changes (triggered by reconnect)
  useEffect(() => {
    const client = getMqttClient();
    clientRef.current = client;

    const onConnect = () => {
      setMqttConnected(true);
      client.subscribe(TOPICS.STATE,      { qos: 0 });
      client.subscribe(TOPICS.CAM_IMG,    { qos: 0 });
      client.subscribe(TOPICS.CAM_STATE,  { qos: 0 });
      client.subscribe(TOPICS.CAM_MOTION, { qos: 0 });
    };

    const onMessage = (topic, payload) => {
      const msg = payload.toString();
      if (topic === TOPICS.STATE) {
        try {
          const data = JSON.parse(msg);
          setTelemetry(data);
          setLastUpdate(new Date());
          setHistory((h) => {
            const point = { ...data, _ts: Date.now() };
            return [...h.slice(-MAX_HISTORY), point];
          });
          // ── Alert checking (5-minute cooldown per type) ──
          const s = settingsRef.current;
          const now = Date.now();
          const cd = alertCooldown.current;
          const canAlert = (key) => !cd[key] || now - cd[key] > 300000;
          if (s.alertTemp && data.temperature >= (s.fanCritical ?? 65) && canAlert("temp")) {
            cd.temp = now;
            toast.error(`Temperature critical: ${data.temperature}°C`, { description: "Above critical threshold" });
          }
          if (s.alertPower && data.total_power >= 4500 && canAlert("power")) {
            cd.power = now;
            toast.warning(`High power: ${Math.round(data.total_power)} W`, { description: "Load exceeds 4.5 kW" });
          }
          if (s.alertGas && data.gas_quality === "DANGER" && canAlert("gas")) {
            cd.gas = now;
            toast.error(`Air quality DANGER! ${data.gas_ppm != null ? Math.round(data.gas_ppm) + " ppm" : ""}`, { description: "MQ-135 - ventilate area immediately" });
          }
        } catch { /* malformed JSON */ }
      } else if (topic === TOPICS.CAM_IMG) {
        setCamImage(`data:image/jpeg;base64,${msg}`);
      } else if (topic === TOPICS.CAM_STATE) {
        try { setCamState(JSON.parse(msg)); } catch { /* ignore */ }
      } else if (topic === TOPICS.CAM_MOTION) {
        try {
          const m = JSON.parse(msg);
          if (m.motion) {
            setLastMotion(new Date());
            toast.warning("Motion detected!", { description: "Camera captured movement" });
          }
        } catch { /* ignore */ }
      }
    };

    const onDisconnect = () => {
      setMqttConnected(false);
      if (settingsRef.current.alertMqtt) toast.warning("MQTT disconnected", { description: "Reconnecting…" });
    };
    const onError = () => setMqttConnected(false);

    client.on("connect",    onConnect);
    client.on("message",    onMessage);
    client.on("disconnect", onDisconnect);
    client.on("error",      onError);
    client.on("offline",    onDisconnect);

    // If already connected (singleton reuse)
    if (client.connected) onConnect();

    return () => {
      client.off("connect",    onConnect);
      client.off("message",    onMessage);
      client.off("disconnect", onDisconnect);
      client.off("error",      onError);
      client.off("offline",    onDisconnect);
    };
  }, [mqttRevision]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTheme    = useCallback(() => setTheme((t) => t === "dark" ? "light" : "dark"), []);
  const updateSettings = useCallback((patch) => setSettings((s) => ({ ...s, ...patch })), []);
  const resetSettings  = useCallback(() => { setSettings({ ...DEFAULT_SETTINGS }); toast.success("Settings reset to defaults"); }, []);

  const reconnect = useCallback((host, user, pass) => {
    setMqttConnected(false);
    mqttReconnect(host, user, pass);
    setMqttRevision((r) => r + 1);
    toast.info("Reconnecting to MQTT…");
  }, []);

  const sendCommand = useCallback(async (device, action, label) => {
    try {
      publishCmd(device, action);
      toast.success(`Sent → ${label || `${device}: ${action}`}`, { description: "Published to MQTT" });
    } catch {
      toast.error("Command failed - MQTT offline");
    }
  }, []);

  const sendCamCommand = useCallback(async (cmd) => {
    try {
      publishCamCmd(cmd);
      toast.success(`Camera: ${cmd}`);
    } catch {
      toast.error("Camera command failed - MQTT offline");
    }
  }, []);

  const addSnapshot   = useCallback((dataUrl) => {
    const item = { id: Date.now().toString(), url: dataUrl, ts: new Date().toISOString() };
    setSnapshots((s) => [item, ...s]);
    toast.success("Snapshot saved to gallery");
  }, []);
  const deleteSnapshot = useCallback((id) => setSnapshots((s) => s.filter((x) => x.id !== id)), []);
  const clearSnapshots = useCallback(() => setSnapshots([]), []);

  // Helper: history formatted for charts (last N points)
  const getHistory = useCallback((range = "1h") => {
    const minutes = range === "24h" ? 1440 : range === "7d" ? 10080 : 60;
    const since = Date.now() - minutes * 60 * 1000;
    return history.filter((p) => p._ts > since);
  }, [history]);

  const value = {
    theme, toggleTheme,
    telemetry, mqttConnected, lastUpdate,
    history, getHistory,
    settings, updateSettings, resetSettings, reconnect,
    sendCommand, sendCamCommand,
    snapshots, addSnapshot, deleteSnapshot, clearSnapshots,
    camImage, camState, lastMotion,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
